/**
 * Simple caching service for API responses
 * Provides TTL-based caching with automatic cleanup
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class CacheService {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly CLEANUP_INTERVAL = 60 * 1000; // 1 minute

  constructor() {
    // Periodic cleanup of expired entries
    setInterval(() => {
      this.cleanup();
    }, this.CLEANUP_INTERVAL);
  }

  /**
   * Get cached data if it exists and hasn't expired
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Set data in cache with optional TTL
   */
  set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * Remove specific entry from cache
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys())
    };
  }

  /**
   * Remove expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));
  }
}

// Create singleton instance
export const cacheService = new CacheService();

// Cache key generators
export const cacheKeys = {
  meetup: (id: string) => `meetup:${id}`,
  meetupParticipants: (id: string) => `meetup:${id}:participants`,
  meetupPlaceDetails: (id: string) => `meetup:${id}:place-details`,
  userProfile: (id: string) => `user:${id}:profile`
};

// Cache TTL constants
export const CACHE_TTL = {
  MEETUP: 10 * 60 * 1000, // 10 minutes
  PARTICIPANTS: 5 * 60 * 1000, // 5 minutes
  PLACE_DETAILS: 30 * 60 * 1000, // 30 minutes
  USER_PROFILE: 60 * 60 * 1000 // 1 hour
};